import { useState } from 'react';

import { Button } from '@clay-run/glaze/button';
import { InputField } from '@clay-run/glaze/input-field';
import { RadioGroupField } from '@clay-run/glaze/radio-group-field';
import { Text } from '@clay-run/glaze/text';

import type { GongFlowSummary } from '../../_shared/flow/gong';
// v1 data constant, not a component — plain array reused for real product
// copy fidelity ("Terra Co-Pilot" is the actual label, not something to
// re-derive). Key Decision 2 forbids sharing RENDER components between v1
// and v2, not plain data.
import { CHANNELS } from '../../_shared/launcher-copy-tokens';
import type { LauncherChannel } from '../schemas';
import {
  STATIC_EMAIL_SENDER_OPTION,
  resolveSenderChoice,
  senderChoiceFromSender,
  type SenderChoice,
} from '../sender-choice';
import { LauncherGongFlowBrowse } from './LauncherGongFlowBrowse';
import { SenderChoiceFields } from './play-flow/SenderChoiceFields';

/**
 * Round-3-QA-fixes plan (decision 5) — Gong is no longer filtered out of
 * this create form's channel options. Picking it swaps in Flow's real
 * browse panel (`LauncherGongFlowBrowse`, round-3b QA fixes G3 — same panel
 * `SequenceMappingDrawer`'s Gong tab opens) — a Gong-channel sequence is now
 * creatable from a real flow pick right here, superseding the old D3
 * restriction (live-QA round 2, Batch 5) that required going through a
 * Playbook's Change-sequence panel first.
 */
const CHANNEL_OPTIONS = CHANNELS.map((c) => ({
  value: c.value,
  label: c.label,
}));

export interface SequenceCreateFieldsProps {
  saving: boolean;
  /**
   * Playbooks QA round 4, U9 (SN1) / Decision 3 — `sender` is now part of a
   * sequence's IDENTITY, so it is collected here, at create, alongside
   * `channel`. `null` means "no sender chosen" — a legal, representable state
   * ("Not set until edited", Decision 10), not a placeholder.
   */
  onSave: (
    name: string,
    channel: LauncherChannel,
    sender: string | null,
    flow?: GongFlowSummary,
  ) => void;
  onCancel: () => void;
  /**
   * R5-11 — `SequenceMappingDrawer`'s Gong tab "Browse Gong flows" doorway
   * opens this form pre-filled with the flow the rep just picked, instead of
   * silently creating a null-sender sequence and bouncing back to the
   * picker. Optional, both default to today's blank state — the OTHER
   * mount of this component (`PlaybooksConfigPage.tsx`'s plain "+ New
   * Sequence" drawer) never passes either and is unaffected.
   */
  initialChannel?: LauncherChannel;
  /** Paired with `initialChannel` — see that prop's doc. */
  initialFlow?: GongFlowSummary;
}

/**
 * Round-3-QA-fixes plan (U4, decision 4) — lifted VERBATIM out of
 * `PlaybooksConfigPage.tsx` (where it was a private, unexported
 * `SequenceCreateFields`) so `SequenceMappingDrawer.tsx`'s "Create a new
 * Sequence" doorway can share the exact same form instead of maintaining its
 * own reduced copy (`CreateSequenceFields`, deleted by this same unit). This
 * is a MOVE, not a rewrite — Config's own coding-standards anti-pattern #2
 * names the pre-lift state ("a private, unexported form component that a
 * second surface then re-implements") as the bug this fixes.
 *
 * E1 step 3 — the "+ New Sequence" create drawer's body. Reuses
 * `NameDrawerField`'s exact shape (label "Name", Cancel/Save, Save disabled
 * until legal) plus one field `NameDrawerField` never needed: `channel` is
 * required with no default on `POST /v2/sequences` (identity-INVARIANT, Key
 * Decision 5 — see `mutations.ts`'s `createSequence`), so Save stays disabled
 * until both a name AND a channel are picked, same disabled-until-legal rule.
 *
 * Round-3-QA-fixes plan (decision 5) — picking the Gong Engage channel
 * additionally requires a picked flow: below the radios, either the picked
 * flow's name (with an inline "Change" that clears it) or a "Pick a Gong
 * flow" button opening `LauncherGongFlowBrowse` renders. Picking a flow
 * prefills the Name field UNLESS the rep has already typed one
 * (`nameTouched`), matching the existing "the Name field is the source of
 * truth once touched" pattern.
 *
 * Playbooks QA round 4, U9 (SN1) / Decision 3 — `SenderChoiceFields` renders
 * BELOW the channel radios (and below the Gong flow picker, which belongs to
 * the channel decision): channel first, as today, because sender is
 * channel-agnostic. Sender is OPTIONAL, unlike `channel` — Save stays enabled
 * with no sender at all, and the rung's readiness badge continues to be what
 * flags a missing one. What Save DOES refuse is a half-typed literal address:
 * an invalid `static_email` entry disables Save with the inline error, while a
 * BLANK one is treated as "no sender" and posts `null`.
 */
export function SequenceCreateFields({
  saving,
  onSave,
  onCancel,
  initialChannel,
  initialFlow,
}: SequenceCreateFieldsProps) {
  const [name, setName] = useState(() => initialFlow?.name ?? '');
  const [nameTouched, setNameTouched] = useState(false);
  const [channel, setChannel] = useState<LauncherChannel | null>(initialChannel ?? null);
  const [pickedFlow, setPickedFlow] = useState<GongFlowSummary | null>(initialFlow ?? null);
  /** Round-3b QA fixes, G3 — whether Flow's real Gong browse panel is open. */
  const [gongBrowseOpen, setGongBrowseOpen] = useState(false);
  /**
   * U9 (SN1) — seeded from `null`, which `senderChoiceFromSender` maps to the
   * static-email option with an EMPTY field rather than pre-selecting a token
   * (defaulting a sender would be a hardcoded fallback for exactly the
   * decision this field exists to capture — see that function's own doc).
   */
  const [senderChoice, setSenderChoice] = useState<SenderChoice>(() => senderChoiceFromSender(null));
  const [senderTouched, setSenderTouched] = useState(false);
  const trimmed = name.trim();

  /**
   * U9 (SN1) — three states, not two:
   *  - UNSET: the static-email option with a blank field. Legal; posts `null`.
   *  - resolved: a token, or a valid literal address. Posts that value.
   *  - invalid: a non-empty literal address that isn't one. Blocks Save.
   *
   * `senderUnset` deliberately keys on the blank FIELD rather than on
   * `senderTouched`: a rep who types an address and then clears it is back to
   * "no sender", which is a real choice, not a validation failure.
   */
  const senderUnset =
    senderChoice.option === STATIC_EMAIL_SENDER_OPTION && senderChoice.email.trim() === '';
  const senderResult = resolveSenderChoice(senderChoice);
  const senderError = senderUnset || senderResult.ok ? null : senderResult.error;
  const sender: string | null = senderUnset ? null : senderResult.ok ? senderResult.sender : null;
  // Both legal — Save stays disabled (not just click-refused) until both are
  // set, same "nothing to interrogate on click" reasoning `NameDrawerField`
  // already applies to a blank name. A Gong channel additionally needs a
  // picked flow — there is no such thing as a flow-less Gong sequence born
  // from this form.
  const canSave =
    trimmed.length > 0 &&
    channel !== null &&
    (channel !== 'gong_engage' || pickedFlow !== null) &&
    // U9 (SN1) — an unset sender is fine; a half-typed one is not.
    senderError === null;

  return (
    <>
      <InputField
        label="Name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setNameTouched(true);
        }}
        autoFocus
      />
      <RadioGroupField
        label="Channel"
        value={channel}
        onValueChange={(value) => {
          setChannel(value as LauncherChannel);
          // Switching away from Gong drops whatever flow was picked — a
          // picked-flow-for-a-different-channel state has no meaning.
          if (value !== 'gong_engage') setPickedFlow(null);
        }}
        options={CHANNEL_OPTIONS}
      />
      {channel === 'gong_engage' &&
        (pickedFlow ? (
          <div className="flex items-center gap-2">
            <Text size="sm">Gong flow: {pickedFlow.name}</Text>
            <Button variant="inline-text" size="xs" onClick={() => setPickedFlow(null)}>
              Change
            </Button>
          </div>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={() => setGongBrowseOpen(true)}>
              Pick a Gong flow
            </Button>
            {gongBrowseOpen && (
              <LauncherGongFlowBrowse
                onSelect={(flow) => {
                  setGongBrowseOpen(false);
                  setPickedFlow(flow);
                  if (!nameTouched) setName(flow.name);
                }}
                onClose={() => setGongBrowseOpen(false)}
                backLabel="← New Sequence"
              />
            )}
          </>
        ))}
      {/* U9 (SN1) — below the channel radios per the plan's own
          Deferred-to-Implementation default: channel first (as today), sender
          after, since sender is channel-agnostic. */}
      <SenderChoiceFields
        value={senderChoice}
        onChange={(next) => {
          setSenderChoice(next);
          setSenderTouched(true);
        }}
        error={senderTouched ? senderError : null}
      />
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canSave}
          loading={saving}
          onClick={() =>
            channel !== null && onSave(trimmed, channel, sender, pickedFlow ?? undefined)
          }
        >
          Save
        </Button>
      </div>
    </>
  );
}
